import { useState, useEffect } from 'react';
import { BASE_URL } from '../../apiConfig';
import { useAuth } from '../../context/AuthProvider';
import { useLanguage } from "../../context/LanguageProvider";
import { X, User, Phone, DollarSign, Loader2 } from 'lucide-react';
import { MdOutlineDeliveryDining as Moto } from "react-icons/md";

export default function LivreurSelectionModal({ isOpen, onClose, selectedOrders, onAssign }) {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLivreur, setSelectedLivreur] = useState(null);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchLivreurs();
    }
  }, [isOpen]);

  const fetchLivreurs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/livreurs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLivreurs(data);
      }
    } catch (error) {
      console.error('Error fetching livreurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedLivreur) return;

    setAssigning(true);
    try {
      await onAssign(selectedLivreur.id);
      onClose();
      setSelectedLivreur(null);
    } catch (error) {
      console.error('Error assigning orders:', error);
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--color-primary-100)] rounded-lg">
              <Moto className="w-6 h-6 text-[var(--color-primary-500)]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('modals.livreur_selection.title')}</h2>
              <p className="text-sm text-gray-500">{t('modals.livreur_selection.orders_selected', { count: selectedOrders.length })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[var(--color-primary-500)] animate-spin" />
            </div>
          ) : livreurs.length === 0 ? (
            <div className="text-center py-12">
              <Moto className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('modals.livreur_selection.no_livreur')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {livreurs.map((livreur) => (
                <div
                  key={livreur.id}
                  onClick={() => setSelectedLivreur(livreur)}
                  className={`p-3 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    selectedLivreur?.id === livreur.id
                      ? 'border-[var(--color-primary-500)] bg-[var(--color-primary-50)]'
                      : 'border-gray-200 hover:border-[var(--color-primary-300)] hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`p-2 rounded-lg ${
                      selectedLivreur?.id === livreur.id
                        ? 'bg-[var(--color-primary-100)]'
                        : 'bg-gray-100'
                    }`}>
                      <Moto className={`w-6 h-6 ${
                        selectedLivreur?.id === livreur.id
                          ? 'text-[var(--color-primary-500)]'
                          : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{livreur.name}</h3>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                          <span>{livreur.prix_delivery} DH / livraison</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
          {t('modals.livreur_selection.cancel')}
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedLivreur || assigning}
            className={`px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              !selectedLivreur || assigning
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] shadow-lg hover:shadow-xl'
            }`}
          >
            {assigning ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('modals.livreur_selection.assigning')}
              </span>
            ) : (
              t('modals.livreur_selection.confirm_assign')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
